import { CSSProperties } from 'react';
import { TFunction } from 'i18next';
import { lightBlue } from '@mui/material/colors';
import { Theme } from '@mui/material';
import { PartnerProfileResourceType, PartnerResourceTranslation } from '../models/PartnerProfile';

/**
 * Returns the i18n badge text for a given partner resource type.
 */
export const getResourceBadgeText = (
  resourceType: PartnerProfileResourceType,
  t: TFunction,
): string => {
  switch (resourceType) {
    case PartnerProfileResourceType.ARTICLE:
      return t('partnerDetails.resourceBadge.article');
    case PartnerProfileResourceType.VIDEO:
      return t('partnerDetails.resourceBadge.video');
    case PartnerProfileResourceType.PDF:
      return t('partnerDetails.resourceBadge.pdf');
    case PartnerProfileResourceType.DOC:
      return t('partnerDetails.resourceBadge.document');
    case PartnerProfileResourceType.CASE_STUDY:
      return t('partnerDetails.resourceBadge.caseStudy');
    default:
      return t('partnerDetails.resourceBadge.resource');
  }
};

/**
 * Returns badge color styles for a given partner resource type.
 */
export const getResourceBadgeStyle = (
  resourceType: PartnerProfileResourceType,
  theme: Theme,
): CSSProperties => {
  switch (resourceType) {
    case PartnerProfileResourceType.VIDEO:
      return {
        backgroundColor: lightBlue[50],
        borderColor: lightBlue[100],
        color: lightBlue[800],
      };
    case PartnerProfileResourceType.PDF:
    case PartnerProfileResourceType.DOC:
      return {
        backgroundColor: theme.palette.grey[50],
        borderColor: theme.palette.grey[200],
        color: theme.palette.grey[700],
      };
    default:
      return {};
  }
};

/**
 * Determines if a resource should be previewed in a dialog.
 * PDFs and DOCs are always previewable.
 * Case studies and articles are only previewable if they are internal links (starting with '/').
 * Videos are handled separately.
 */
export const isResourcePreviewableInDialog = (
  resourceType: PartnerProfileResourceType,
  resourceLink: string,
): boolean => {
  // PDFs and DOCs are always previewable in dialog
  if (resourceType === PartnerProfileResourceType.PDF ||
      resourceType === PartnerProfileResourceType.DOC) {
    return true;
  }
  // Case studies and articles are only previewable in dialog if they're internal links
  if (resourceType === PartnerProfileResourceType.CASE_STUDY ||
      resourceType === PartnerProfileResourceType.ARTICLE) {
    return resourceLink.startsWith('/');
  }
  return false;
};

/**
 * Determines if a resource can be shown in a dialog (either video or preview dialog).
 */
export const isResourceDialogCompatible = (resource: PartnerResourceTranslation): boolean => (
  resource.resourceType === PartnerProfileResourceType.VIDEO ||
  isResourcePreviewableInDialog(resource.resourceType, resource.resourceLink)
);

/**
 * Checks if a URL is an internal article link (e.g., /content/123).
 */
export const isInternalArticleLink = (url: string): boolean => {
  if (!url) return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.pathname.startsWith('/content/');
  } catch {
    return url.startsWith('/content/');
  }
};

/**
 * Extracts the content ID from an internal article URL.
 * Returns null if the URL is not a valid article link.
 */
export const extractArticleId = (url: string): number | null => {
  if (!url) return null;
  try {
    const urlObj = new URL(url, window.location.origin);
    const match = urlObj.pathname.match(/^\/content\/(\d+)/);
    return match ? Number(match[1]) : null;
  } catch {
    const match = url.match(/^\/content\/(\d+)/);
    return match ? Number(match[1]) : null;
  }
};
